import { GroupTypes } from '@/interfaces/enums';
import { ProductProps } from '@/interfaces/models/product';
import Image from '@/models/image';
import IngredientGroup from '@/models/ingredientGroup';
import assetsUtils from '@/utils/url.utils';
import Ingredient from './ingredient';

export default class Product {
  public id: string = '';
  public category: { id: string; name: string; } | null = null;
  public cost: number = 0;
  public description: string = '';
  public groups: IngredientGroup[] = [];
  public image: Image = new Image();
  public isFavorite: boolean = false;
  public isShow: boolean = true;
  public isShowInPos: boolean = true;
  public isRequiresGtin: boolean = false;
  public priority: number = 0;
  public title: string = '';
  public weight: string = '';
  projectProduct: { id: string } | null = null;

  [key: string]: any;

  public constructor() {
    this.groups = [
      IngredientGroup.create(GroupTypes.REQUIRED, 1, null),
      IngredientGroup.create(GroupTypes.OPTIONAL, 2, null),
      IngredientGroup.create(GroupTypes.ONE_OF, 3, 'Один из')
    ];
  }

  public static createFromRequest(productProps: ProductProps) {
    const product = new Product();

    product.category = productProps.category;
    product.cost = productProps.cost;
    product.description = productProps.description;
    product.groups = productProps.groups
      .map(IngredientGroup.createByProps)
      .sort((firstGroup: IngredientGroup, secondGroup: IngredientGroup) => {
        return firstGroup.priority > secondGroup.priority ? 1 : -1;
      });
    product.id = productProps.id;

    if (productProps.image) {
      product.image = new Image(productProps.image);
    }

    product.isFavorite = productProps.isFavorite;
    product.isShow = productProps.isShow;
    product.isShowInPos = productProps.isShowInPos;
    product.isRequiresGtin = productProps.isRequiresGtin || false;
    product.priority = productProps.priority;
    product.title = productProps.title;
    product.weight = productProps.weight;
    product.projectProduct = productProps.projectProduct;

    return product;
  }

  public static createByCategory(category: { id: string, name: string }) {
    const product = new Product();

    product.category = {
      id: category.id,
      name: category.name
    };

    return product;
  }

  get ingredients(): Ingredient[] {
    return this.groups
      .reduce((ingredients: Ingredient[], group: IngredientGroup) => {
        ingredients.push(...group.ingredients);

        return ingredients;
      }, []);
  }

  get isHaveNotEmptyGroups(): boolean {
    return this.notEmptyGroups.length > 0;
  }

  get notEmptyGroups(): IngredientGroup[] {
    return this.groups.filter(({ ingredients }: IngredientGroup) => ingredients.length > 0);
  }

  get isHaveEmptyOneOf(): boolean {
    return this.groups.filter(
      ({ type, ingredients }: IngredientGroup) => GroupTypes.ONE_OF === type && ingredients.length === 0
    ).length > 0;
  }

  get isHaveOptionalIngredients(): boolean {
    return this.optionalIngredients.length > 0;
  }

  get isHaveOneOfIngredients(): boolean {
    return this.notEmptyGroups.some((group: IngredientGroup) => GroupTypes.ONE_OF === group.type);
  }

  get isHaveRequiredIngredients(): boolean {
    return this.notEmptyGroups.some((group: IngredientGroup) => GroupTypes.REQUIRED === group.type);
  }

  get notRequiredIngredients(): Ingredient[] {
    const oneOfIngredients = this.oneOfGroups.reduce(
      (ingredients: Ingredient[], group: IngredientGroup) =>ingredients.concat(group.ingredients),
      []
    );

    return oneOfIngredients.concat(this.optionalIngredients);
  }

  get optionalIngredients(): Ingredient[] {
    const optionalGroup = this.notEmptyGroups.find(
      ({ type }: IngredientGroup) => GroupTypes.OPTIONAL === type
    );

    if (undefined !== optionalGroup) {
      return optionalGroup.ingredients;
    }

    return [];
  }

  get oneOfGroups(): IngredientGroup[] {
    return this.notEmptyGroups.filter(({ type }: IngredientGroup) => GroupTypes.ONE_OF === type);
  }

  get preview(): URL {
    if (this.image.original && this.image.path) {
      return assetsUtils.generateAssetsUrl(this.image.path);
    }

    return require('../assets/images/product-placeholder.png');
  }

  public get isAllIngredientsHaveUnitCountAndCost(): boolean {
    return this.groups.every((group: IngredientGroup) => {
      if (GroupTypes.REQUIRED === group.type) {
        return group.ingredients.every((ingredient: Ingredient) => {
          return ingredient.unitCount != null && ingredient.unitCount > 0;
        });
      }

      return group.ingredients.every((ingredient: Ingredient) => {
        return ingredient.unitCount != null
          && ingredient.cost != null
          && ingredient.unitCount > 0
          && ingredient.cost >= 0;
      });
    });
  }

  mergeWithNewFields(productFields: ProductProps) {
    for (let key in productFields) {
      if (productFields.hasOwnProperty(key) && this.hasOwnProperty(key)) {
        switch (key) {
          case 'image': {
            if (productFields.image) {
              this.image = new Image(productFields.image);
            } else {
              this.image = new Image();
            }

            break;
          }

          case 'groups': {
            this.groups = productFields.groups.map(IngredientGroup.createByProps);

            break;
          }

          default: {
            this[key] = productFields[key];
          }
        }
      }
    }
  }

  removeAllIngredients() {
    for (let index = 0; index < this.groups.length; index++) {
      if (GroupTypes.ONE_OF === this.groups[index].type && this.oneOfGroups.length > 1) {
        this.groups.splice(index, 1);
      } else {
        this.groups[index].ingredients = [];
        this.groups[index].name = null;
      }
    }

    this.rebuildGroupsPriority();
  }

  addIngredient(groupId: string, ingredient: Ingredient) {
    const group: IngredientGroup | undefined = this.groups.find(
      ({ id }: IngredientGroup) => id === groupId
    );

    if (undefined !== group) {
      group.ingredients.push(ingredient);
    }
  }

  addGroup(group: IngredientGroup) {
    this.groups.push(group);
  }

  moveIngredientFromOldToNewGroup(movingIngredient: Ingredient, oldGroupId: string, newGroupId: string) {
    const oldGroup: IngredientGroup | undefined = this.groups.find(
      ({ id }: IngredientGroup) => id === oldGroupId
    );

    if (undefined !== oldGroup) {
      const ingredient = oldGroup.ingredients.find(
        ({ hash }: Ingredient) => hash === movingIngredient.hash
      );

      this.removeIngredient(movingIngredient, oldGroupId);

      if (undefined !== ingredient) {
        const newGroup: IngredientGroup | undefined = this.groups.find(
          ({ id }: IngredientGroup) => id === newGroupId
        );

        if (undefined !== newGroup) {
          newGroup.ingredients.push(ingredient);
        }
      }
    }
  }

  removeIngredient(ingredient: Ingredient, oldGroupId: string) {
    const oldGroupIndex: number = this.groups.findIndex(
      ({ id }: IngredientGroup) => id === oldGroupId
    );

    if (-1 !== oldGroupIndex) {
      if (
        GroupTypes.ONE_OF === this.groups[oldGroupIndex].type
        && 1 === this.groups[oldGroupIndex].ingredients.length
        && this.oneOfGroups.length > 1
      ) {
        this.groups.splice(oldGroupIndex, 1);
      } else {
        const ingredientIndex = this.groups[oldGroupIndex].ingredients.findIndex(
          ({ hash }: Ingredient) => hash === ingredient.hash
        );

        if (-1 !== ingredientIndex) {
          this.groups[oldGroupIndex].ingredients.splice(ingredientIndex, 1);

          if (this.groups[oldGroupIndex].ingredients.length === 0) {
            this.groups[oldGroupIndex].name = null;
          }
        }
      }
    }

    this.rebuildGroupsPriority();
  }

  private rebuildGroupsPriority() {
    let oneOfMinimumPriority = 3;

    for (const group of this.groups) {
      if (GroupTypes.REQUIRED === group.type) {
        group.priority = 1;
      } else if (GroupTypes.OPTIONAL === group.type) {
        group.priority = 2;
      } else {
        group.priority = oneOfMinimumPriority++;
      }
    }
  }

  removeImage() {
    this.image = new Image();
  }

  toggleIsFavorite() {
    this.isFavorite = !this.isFavorite;
  }

  toggleIsShow() {
    this.isShow = !this.isShow;
  }

  toggleIsShowInPos() {
    this.isShowInPos = !this.isShowInPos;
  }

  showInPos() {
    this.isShowInPos = true;
  }

  hideInPos() {
    this.isShowInPos = false;
  }

  showInWidget() {
    this.isShow = true;
  }

  hideInWidget() {
    this.isShow = false;
  }
}
